import {useQuery} from '@tanstack/react-query'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {fetchOneEntry} from '@builder.io/sdk-react'
import {responseSizeAcceptable} from 'hec/utils/responseUtils.tsx'
import {isValidBuilderOptions} from 'hec/utils/common'

// A more Hooks-based approach, for React-style fetching, must render the Content component manually
// Pros: Get to choose Content props manually

export const useFetchOneEntry = ({queryKey, options, enabled = true}) => {
    const apiKey = getConfig().app.builder.api

    const userAttributes = {}

    // Add validation for required options
    const isValidOptions = isValidBuilderOptions(options)
    const shouldActuallyFetch = enabled && isValidOptions

    // NOTE: Builder requires non-empty userAttributes to evaluate scheduling (startDate/endDate).
    // If you need scheduled content, pass something like this when calling this hook:
    //   userAttributes: { date: Date.now() }
    // Otherwise, Builder may return the top entry regardless of schedule.
    if (isValidOptions) {
        options.userAttributes = {
            ...userAttributes,
            ...options.userAttributes
        }
    }

    // Log warning if options are invalid but fetch was enabled
    if (enabled && !isValidOptions) {
        console.warn('useFetchOneEntry: Invalid options provided', {
            options,
            enabled,
            modelType: typeof options?.model,
            queryKey
        })
    }

    const query = useQuery({
        queryKey: [
            ...(typeof queryKey === 'string'
                ? [queryKey]
                : Array.isArray(queryKey)
                ? queryKey
                : ['Builder-Fetch-Content'])
        ],
        queryFn: async () => {
            // Double-check validation before making API call
            if (!options?.model) {
                throw new Error('Model is required for Builder.io API call')
            }

            const response = await fetchOneEntry({
                apiKey,
                ...options
            })
            if (responseSizeAcceptable(response, [queryKey, options])) {
                return response
            } else {
                console.error(new Error('Response size exceeds acceptable limits'))
                return null
            }
        },
        enabled: shouldActuallyFetch
    })

    return {...query, apiKey}
}
